<template>
  <Drawer v-model:visible="store.drawerVisible" position="right" data-testid="whats-new-drawer"
    :style="{ width: '36rem', maxWidth: '100vw' }">
    <template #header>
      <div v-if="store.view === 'entry' && selectedEntry" class="flex items-center gap-3">
        <Button label="All updates" icon="pi pi-arrow-left" text size="small" data-testid="whats-new-all-updates"
          @click="store.openChangelog()" />
        <span class="text-sm text-surface-500">{{ formatWhatsNewDate(selectedEntry.date) }}</span>
      </div>
      <h2 v-else class="text-lg font-semibold text-surface-900">What's new</h2>
    </template>

    <article v-if="store.view === 'entry' && selectedEntry" data-testid="whats-new-entry" class="flex flex-col gap-5 pb-6">
      <header class="flex flex-col gap-2">
        <h3 class="text-2xl font-semibold text-surface-900">{{ selectedEntry.title }}</h3>
        <p class="text-surface-700 leading-relaxed">{{ selectedEntry.summary }}</p>
      </header>

      <section v-for="(section, index) in selectedEntry.sections" :key="index" class="flex flex-col gap-2">
        <h4 v-if="section.heading" class="text-base font-semibold text-surface-900">{{ section.heading }}</h4>
        <p v-for="(paragraph, p) in section.paragraphs" :key="`p${p}`" class="text-surface-700 leading-relaxed">{{ paragraph }}</p>
        <ul v-if="section.bullets.length" class="list-disc pl-5 flex flex-col gap-1.5 text-surface-700 leading-relaxed">
          <li v-for="(bullet, b) in section.bullets" :key="`b${b}`">{{ bullet }}</li>
        </ul>
      </section>

      <div v-if="selectedEntry.action">
        <Button :label="selectedEntry.action.label" icon="pi pi-arrow-right" iconPos="right" data-testid="whats-new-action"
          @click="go(selectedEntry.action.path)" />
      </div>
    </article>

    <div v-else class="flex flex-col gap-3 pb-6">
      <p class="text-sm text-surface-600">Changes to the platform, newest first. You only see updates for the parts of the app you can use.</p>
      <p v-if="error" class="text-sm text-red-600">Updates could not be loaded. Please try again later.</p>
      <p v-else-if="!entries.length && !loading" class="text-sm text-surface-500">Nothing new yet.</p>
      <button v-for="entry in entries" :key="entry.id" type="button" :data-testid="`whats-new-card-${entry.id}`"
        class="flex flex-col gap-1 rounded-lg border border-surface-200 p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
        @click="store.openEntry(entry.id)">
        <span class="text-xs text-surface-500">{{ formatWhatsNewDate(entry.date) }}</span>
        <span class="text-base font-semibold text-surface-900">{{ entry.title }}</span>
        <span class="text-sm text-surface-600">{{ entry.summary }}</span>
        <span class="text-sm font-medium text-primary">Read more</span>
      </button>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import { useWhatsNew } from '@/composables/whatsNew/useWhatsNew';
import { formatWhatsNewDate } from '@/utils/whatsNew';

/**
 * The changelog drawer: one update at a time, or every update. Mounted once in
 * the admin layout; the sidebar and phone menus open it through the store.
 */
const router = useRouter();
const { entries, selectedEntry, loading, error, store } = useWhatsNew();

const go = (path: string) => {
  store.close();
  router.push(path);
};
</script>
